import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ChartOptions,
  HorizontalBarChartData,
  Utils,
} from 'marketpro-design-lib';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GroupedResponseTwoLevels } from '../../models/grouped-statistics-response.model';
import { SpendingDistribution } from '../../models/spending-distribution.model';
import { SpendingStatisticsResponse } from '../../models/spending-statistics-response.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataService } from '../../services/data.service';
import { SystemGroupsService } from '../../services/system-groups.service';
import { AuthService } from '../../../auth/services/auth.service';
import { CitiesService } from '../../services/cities.service';
import { constants } from 'projects/marketpro-ui/src/app/constants';
import { CategoryService } from '../../services/categories.service';
import { StatisticsService } from '../../services/statistics.service';
import { SpendingGroup } from '../../models/spending-group.model';
import { TotalDistributionElement } from '../../models/statistics.model';
import { debounceTimer } from 'projects/marketpro-design-lib/src';
import { ToggleableNameValuePair } from '../../models/name-value-pair.model';

@Component({
  selector: 'mpro-spending-distribution',
  templateUrl: './spending-distribution.component.html',
  styleUrls: ['./spending-distribution.component.scss'],
})
export class SpendingDistributionComponent implements OnInit, OnDestroy {
  @Input() flag: boolean;
  public isFreemium = false;
  public isCategories: boolean;
  public area: string = 'The World';
  public spendingData: SpendingDistribution[];
  public spendingSelected: string;
  public total: SpendingDistribution = {
    spending_daily: 'Total',
    people: 0,
    spending_ppp: 0,
    spending_share: 100,
    id: 1,
    name: '',
  };

  public selectedSegment?: string;
  public dataFilters: DataFilters = new DataFilters();
  public chartOptions: ChartOptions = {
    colors: [],
  };
  public chartData: HorizontalBarChartData[] = [];
  public getNameValuePair = DataFilters.getNameValuePair;
  public categoriesList: ToggleableNameValuePair[] = [];

  private ngUnsubscribe: Subject<void> = new Subject();
  public loadingCitiySpendingAndDistribution = false;
  constructor(
    private authService: AuthService,
    public dataService: DataService,
    private dataFiltersService: DataFiltersService,
    private systemGroupsService: SystemGroupsService,
    private citiesService: CitiesService,
    private categoryService: CategoryService,
    private statisticsService: StatisticsService
  ) {}

  public ngOnInit(): void {
    this.isCategories = this.flag;
    this.categoryService
      .getUserCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((categories) => (this.categoriesList = categories));
    combineLatest([
      this.dataFiltersService.getDataFilters(),
      this.dataService.getData(),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([filters, data]) => {
        this.dataFilters = filters;
        const current = data.spendingDistributionData.current;

        if (!this.dataFilters.regionSelection.isCitySelected() && current) {
          if ('spendingDatas' in current || 'incomeDatas' in current) {
            const res =
              this.dataFilters.spendingGroup instanceof SpendingGroup
                ? current.spendingDatas
                : current.incomeDatas;

            if (Array.isArray(res)) {
              this.setTotalDistribution(res);
            }
          } else {
            const yearData = current[this.dataFilters.year];
            if (yearData) {
              this.calculateData(yearData);
              this.caluclateChartData();
              this.setSelectedSpendingGroup();
            }
          }
        }

        if (this.dataFilters.regionSelection.isCitySelected()) {
          this.citiesService.getCitySpendingDistribution$.next({
            city: this.dataFilters?.regionSelection?.getName(),
          });
        }

        this.statisticsService.getTotalDistribution$.next();
      });

    this.getCitySpendingDistribution();
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => {
        this.isFreemium = plan.isFreemium;
        if (plan.isFreemium) {
          this.getTotalSpending();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getTotalSpending() {
    this.statisticsService.getTotalDistribution$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(debounceTimer),
        switchMap(() => {
          this.loadingCitiySpendingAndDistribution = true;
          return this.statisticsService
            .getTotalDistribution()
            .pipe(
              tap(() => (this.loadingCitiySpendingAndDistribution = false))
            );
        })
      )
      .subscribe((response) => {
        const res =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? response.spendingDatas
            : response.incomeDatas;
        this.setTotalDistribution(res);
      });
  }

  private parseGroupBounds(groupName: string): { gte?: number; lte?: number } {
    const trimmed = groupName.trim();

    if (trimmed.startsWith('<')) {
      const lte = Number(trimmed.slice(1));
      return { lte: isNaN(lte) ? undefined : lte };
    }

    if (trimmed.startsWith('>')) {
      const gte = Number(trimmed.slice(1));
      return { gte: isNaN(gte) ? undefined : gte };
    }

    if (trimmed.endsWith('+')) {
      const gte = Number(trimmed.replace('+', ''));
      return { gte: isNaN(gte) ? undefined : gte };
    }

    const [minStr, maxStr] = trimmed.split('-');
    const gte = Number(minStr);
    const lte = Number(maxStr);
    return {
      gte: isNaN(gte) ? undefined : gte,
      lte: isNaN(lte) ? undefined : lte,
    };
  }

  private setTotalDistribution(data: TotalDistributionElement[]) {
    this.spendingData = [];
    this.chartData = [];
    this.chartOptions.colors = [];
    const spendingDataTemp: SpendingDistribution[] = data
      .sort((a, b) => {
        const aBounds = this.parseGroupBounds(a.groupName);
        const bBounds = this.parseGroupBounds(b.groupName);
        return (aBounds.gte || 0) - (bBounds.gte || 0);
      })
      .map((total, i) => {
        const { gte, lte } = this.parseGroupBounds(total.groupName);
        this.chartOptions.colors.push(
          this.systemGroupsService.getSpendingGroupColor(gte, lte)
        );
        const spendingGroupName = constants.data.systemSpendingGroups.find(
          (group) => group.lessThan === lte && group.greaterThan === gte
        )?.name;
        total.groupName = this.getSpendingDaily(total.groupName);
        this.chartData.push({
          key: total.groupName,
          value: total.amount,
          tooltip: {
            type: 'chart',
            title: spendingGroupName || '',
            chartText: `${Utils.formatNumber(
              total.population,
              false,
              '.0-0'
            )} people`,
            chartCaption: `${Utils.formatNumber(
              total.amountPercentage
            )}% of ${Utils.formatNumber(data[0].totalPopulation, true)}`,
          },
        });
        return {
          id: i,
          people: total.population,
          spending_daily: total.groupName,
          spending_ppp: total.amount,
          spending_share: total.amountPercentage,
          name: total.groupName,
          color: this.systemGroupsService.getSpendingGroupColor(gte, lte),
        } as SpendingDistribution;
      });
    this.spendingData = spendingDataTemp;
    this.total = {
      ...this.total,
      people: data[0].totalPopulation,
      spending_ppp: data[0].totalAmount,
      spending_share: 100,
    };
  }

  private getCitySpendingDistribution() {
    this.citiesService.getCitySpendingDistribution$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(debounceTimer),
        switchMap(() => {
          this.loadingCitiySpendingAndDistribution = true;
          return this.statisticsService
            .getTotalDistribution()
            .pipe(
              tap(() => (this.loadingCitiySpendingAndDistribution = false))
            );
        })
      )
      .subscribe((response) => {
        const res =
          this.dataFilters.spendingGroup instanceof SpendingGroup
            ? response.spendingDatas
            : response.incomeDatas;
        this.setTotalDistribution(res);
      });
  }

  private calculateData(
    data: GroupedResponseTwoLevels<SpendingStatisticsResponse>
  ) {
    this.spendingData = [];
    let spendingMap: Map<string, SpendingDistribution> = new Map();
    // todo: get rid of ifs
    if (this.dataFilters.gender == 'male') {
      this.calculateSpendingDistribution(
        Object.entries(data['1']),
        spendingMap
      );
    } else if (this.dataFilters.gender == 'female') {
      this.calculateSpendingDistribution(
        Object.entries(data['2']),
        spendingMap
      );
    } else {
      this.calculateSpendingDistribution(
        Object.entries(data['1']),
        spendingMap
      );
      this.calculateSpendingDistribution(
        Object.entries(data['2']),
        spendingMap
      );
    }

    this.calculateTotalAndPercentage(spendingMap);
    let divider = Utils.getDaysInAYear(this.dataFilters.year);
    this.spendingData = Array.from(spendingMap.values()).map((d) => {
      if (d.color) {
        this.chartOptions.colors.push(d.color);
      }
      if (!this.dataFilters.spendingPer) {
        d.spending_ppp /= divider;
      }

      return d;
    });
    if (!this.dataFilters.spendingPer) {
      this.total.spending_ppp /= divider;
    }
  }

  private caluclateChartData(): void {
    this.chartData = [];
    this.spendingData.forEach((spending) => {
      const peopleShare = (spending.people / this.total.people) * 100;
      this.chartData.push({
        key: spending.spending_daily,
        value: spending.spending_share,
        tooltip: {
          type: 'chart',
          title: spending.name || '',
          chartText: `${Utils.formatNumber(
            spending.people,
            false,
            '.0-0'
          )} people`,
          chartCaption: `${Utils.formatNumber(
            peopleShare
          )}% of ${Utils.formatNumber(this.total.people, true)}`,
        },
      });
    });
  }

  private calculateTotalAndPercentage(
    spendingMap: Map<string, SpendingDistribution>
  ) {
    this.total.people = 0;
    this.total.spending_ppp = 0;

    Array.from(spendingMap.values()).forEach((value) => {
      this.total.people += value.people;
      this.total.spending_ppp += value.spending_ppp;
    });

    Array.from(spendingMap.values()).forEach((value) => {
      value.spending_share =
        (value.spending_ppp / this.total.spending_ppp) * 100;
    });
  }
  private getSpendingValue(ppp: number, nominal: number) {
    let value = this.dataFilters.currency ? nominal : ppp;

    return value;
  }
  private calculateSpendingDistribution(
    spendingEntries,
    spendingMap: Map<string, SpendingDistribution>
  ) {
    spendingEntries.map((val) => {
      let existingEntry = spendingMap.get(this.getSpendingDaily(val[0]));
      if (existingEntry) {
        existingEntry.people += val[1].headCount;
        if (this.dataFilters.category && this.dataFilters.category !== '0') {
          existingEntry.spending_ppp += this.categoryService.getCategoryData(
            val[1]
          );
        } else {
          existingEntry.spending_ppp += this.dataFilters.currency
            ? val[1].spending2021ppp
            : val[1].spendingCurrentUsd;
        }
        spendingMap.set(existingEntry.spending_daily, existingEntry);
      } else {
        let spendingDistribution = new SpendingDistribution();
        spendingDistribution.spending_daily = this.getSpendingDaily(val[0]);
        spendingDistribution.id = val[1].id;
        spendingDistribution.people = val[1].headCount;

        if (this.dataFilters.category && this.dataFilters.category !== '0') {
          spendingDistribution.spending_ppp =
            this.categoryService.getCategoryData(val[1]);
        } else {
          spendingDistribution.spending_ppp = this.dataFilters.currency
            ? val[1].spending2021ppp
            : val[1].spendingCurrentUsd;
        }

        // TODO: not very good matching of groups
        const spendingGroup =
          this.systemGroupsService.systemGroups.spendingGroups.find(
            (group) =>
              group.compactDisplayValue().substring(1) ===
              this.getSpendingDaily(val[0])
          );
        spendingDistribution.name = spendingGroup?.name;
        spendingDistribution.color =
          this.systemGroupsService.getSpendingGroupColor(
            spendingGroup?.greaterThan,
            spendingGroup?.lessThan
          );
        spendingMap.set(
          spendingDistribution.spending_daily,
          spendingDistribution
        );
      }
    });
  }

  // Will fix this from backend to return the exact names, this is workaround for now until the backend fix is live.
  private getSpendingDaily(field: string) {
    if (field === '130-null') {
      return '>130';
    }

    if (field === 'null-13' || field === '0-13') {
      return '<13';
    }

    return field;
  }

  private setSelectedSpendingGroup() {
    let spendingData = this.spendingData.find(
      (sd) =>
        sd.spending_daily ===
        this.dataFilters.spendingGroup.compactDisplayValue().substring(1)
    );
    this.spendingSelected = spendingData?.spending_daily || '';
    this.chartOptions = {
      ...this.chartOptions,
      selectedGroup: this.spendingSelected || undefined,
    };
  }
}
